import { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Outlet, useMatch } from 'react-router-dom'
import { SceneErrorIndicatorFallback } from '@scene'
import { Allotment, type AllotmentHandle } from 'allotment'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, IconButton, Tooltip, useTheme } from '@mui/material'
import AIChatDrawerSlot from '@editor/components/AIChatDrawerSlot'
import { ELEMENT_LIST_PANE_DEFAULT_WIDTH } from '@editor/components/ElementListPane'
import { GenericErrorBoundaryFallback } from '@ui/errors'
import { LayoutDrawer } from '@ui/structure'
import { useAppStore } from '@stores'
import {
  selectIsPlanViewSceneCovered,
  useControlStore,
  useEditorViewStore,
  useResultsStore,
} from '@editorStores'
import { useResultsSelection, useIfcImportSelectionActive } from '@editorHooks'
import { SceneControlled } from '@editorComponents'
import useUIStore from 'src/state/stores/ui'
import { GlobalLoadsStiffeningMesh, WindLoadsScene } from '../../pages/GlobalLoads/components'
import GlobalLoadsToolbar from '../../pages/GlobalLoads/components/Toolbar'
import useGlobalLoadsQueryParams from '../../pages/GlobalLoads/hooks/useGlobalLoadsQueryParams'
import IFCImportToolbar from '../../pages/IFCImport/components/Toolbar'
import { PlanPdfViewerProvider } from '../../pages/PlanView/PlanPdfViewerContext'
import PlanPdfPreview from '../../pages/PlanView/components/PlanPdfPreview'
import PlanViewToolbar from '../../pages/PlanView/components/Toolbar'
import AnchorDataGrid2 from '../../pages/Results2/components/AnchorDataGrid2'
import Results2Scene from '../../pages/Results2/components/Results2Scene'
import ResultsToolbar from '../../pages/Results2/components/Toolbar'
import useCornerConnectionAdditionalLoadProposalState from '../../pages/Results2/hooks/useCornerConnectionAdditionalLoadProposalState'
import StructuralPlanningScene from '../../pages/StructuralPlanning/components/StructuralPlanningScene'
import StructuralPlanningToolbar from '../../pages/StructuralPlanning/components/Toolbar'
import { useAllotmentRouteResize } from './hooks/useAllotmentRouteResize'
import { computePaneSizes } from './hooks/useAllotmentRouteResize/utils'

// The Results2 scene-interaction E2E tests click empirically-calibrated canvas coordinates, which
// depend on the scene canvas width. Under Cypress/Playwright, pin the Results2 outlet (and thus the
// scene) to the width those coordinates were captured at so tweaks to the production default don't
// shift the scene and break the tests. See app/playwright/results2-scene-interaction.spec.ts.
const isE2E = typeof window !== 'undefined' && ('Cypress' in window || '__STATIK_E2E__' in window)
const RESULTS_OUTLET_EXTRA_WIDTH = isE2E ? 220 : 135

const ResultsAfterDrawer = (): ReactElement => {
  const selectedProposalAnchor = useResultsStore(state => state.selectedProposalAnchor)
  const { cornerConnectionState } = useCornerConnectionAdditionalLoadProposalState()
  const { selectedConnector, selectResultsConnector } = useResultsSelection()

  const disableAllExcept = useMemo(() => {
    if (cornerConnectionState.type === 'idle') return

    if (
      cornerConnectionState.type === 'selected-anchor' ||
      cornerConnectionState.type === 'selected-anchor-and-wall'
    ) {
      return {
        anchorGuid: cornerConnectionState.anchorGuid,
        fields: [cornerConnectionState.field, 'comment'],
      }
    }

    if (selectedProposalAnchor === cornerConnectionState.anchorGuid) {
      return {
        anchorGuid: cornerConnectionState.anchorGuid,
        fields: [cornerConnectionState.field, 'comment'],
      }
    }
  }, [cornerConnectionState, selectedProposalAnchor])

  return (
    <AnchorDataGrid2
      disallowSelection={cornerConnectionState.type !== 'idle'}
      disableAllExcept={disableAllExcept}
      selectedConnector={selectedConnector}
      onSelectConnector={selectResultsConnector}
    />
  )
}

const EditorSceneLayout = (): ReactElement => {
  const theme = useTheme()
  // Match as prefix since these routes can have nested sub-routes.
  const matchGlobalLoads = useMatch({ path: '/editor/:projectId/global-loads', end: false })
  const matchStructuralPlanning = useMatch({
    path: '/editor/:projectId/structural-planning',
    end: false,
  })
  const matchResults = useMatch({ path: '/editor/:projectId/results', end: false })
  const matchExport = useMatch({ path: '/editor/:projectId/export', end: false })
  const matchIFCImport = useMatch({ path: '/editor/:projectId/ifc-import', end: false })
  const matchPlanView = useMatch({ path: '/editor/:projectId/plan-view', end: false })

  const results2IntegratedAnchorView = useAppStore(state => state.results2IntegratedAnchorView)

  const setIsDrawerExpanded = useControlStore(state => state.setIsDrawerExpanded)
  const lastDrawerExpandedRef = useRef<boolean | null>(null)

  const elementListCollapsed = useControlStore(state => state.elementListCollapsed)
  const setElementListCollapsed = useControlStore(state => state.setElementListCollapsed)
  const resultsListCollapsed = useControlStore(state => state.resultsListCollapsed)
  const setResultsListCollapsed = useControlStore(state => state.setResultsListCollapsed)
  const ifcDrawer = useIfcImportSelectionActive()
  // The AI chat replaces the right drawer's content while it is open.
  const aiChatOpen = useUIStore(state => state.aiChatOpen)

  // The element-list toggle sits on the scene/sidebar border. Its `left` is updated imperatively
  // (see positionToggleButton) so it tracks the Allotment drag frame-for-frame without a per-tick
  // re-render of this layout (which would re-render the whole page via <Outlet>). The boolean only
  // gates the button's first appearance once we have a measured width.
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null)
  const scenePaneWidthRef = useRef<number | null>(null)
  const [toggleReady, setToggleReady] = useState(false)
  // Live width of the outlet (right) pane — read on expand to decide whether to widen it.
  const outletWidthRef = useRef<number | null>(null)

  const positionToggleButton = useCallback((left: number) => {
    scenePaneWidthRef.current = left
    if (toggleButtonRef.current) toggleButtonRef.current.style.left = `${left}px`
  }, [])

  const drawerWidth = Number(theme.distances.editorDrawerWidthExpanded)
  const drawerCollapsedWidth = Number(theme.distances.editorDrawerWidth)

  // Outlet width that comfortably holds the element list (default width) + the detail drawer (the
  // default drawer width). When the list is expanded from a narrower outlet, the outlet grows to at
  // least this — single source of truth from the theme distances.
  const expandedOutletWidth = drawerCollapsedWidth + ELEMENT_LIST_PANE_DEFAULT_WIDTH
  // Results2's outlet holds its own list + detail panes; reuse the same width it starts at.
  const resultsOutletWidth = drawerWidth + RESULTS_OUTLET_EXTRA_WIDTH

  /** Same idea as the old expand/collapse control: theme collapsed vs expanded drawer widths. */
  const drawerExpandedWidthThreshold = useMemo(() => {
    return (drawerCollapsedWidth + drawerWidth) / 2
  }, [drawerCollapsedWidth, drawerWidth])

  const handleAllotmentChange = useCallback(
    (sizes: number[]) => {
      const leftPaneWidth = sizes[0]
      if (leftPaneWidth !== undefined) {
        positionToggleButton(leftPaneWidth)
        setToggleReady(prev => prev || true)
      }

      const rightPaneWidth = sizes[1]
      if (rightPaneWidth === undefined) return
      outletWidthRef.current = rightPaneWidth

      const nextExpanded = rightPaneWidth >= drawerExpandedWidthThreshold
      if (lastDrawerExpandedRef.current === nextExpanded) return
      lastDrawerExpandedRef.current = nextExpanded
      setIsDrawerExpanded(nextExpanded)
    },
    [drawerExpandedWidthThreshold, setIsDrawerExpanded, positionToggleButton],
  )

  const { params: { tab } = { tab: undefined } } = useGlobalLoadsQueryParams()

  const containerRef = useRef<HTMLDivElement | null>(null)
  const allotmentRef = useRef<AllotmentHandle | null>(null)

  const globalLoadsScene = useMemo(() => {
    if (!matchGlobalLoads) return null
    return (
      <>
        {/* The stiffening segments back the seismic storey stiffnesses, so they stay visible
            across every loads tab. */}
        <GlobalLoadsStiffeningMesh />
        {tab === 'wind-loads' && <WindLoadsScene />}
      </>
    )
  }, [matchGlobalLoads, tab])

  const scene = useMemo(() => {
    if (matchResults) return <Results2Scene />
    if (matchStructuralPlanning) return <StructuralPlanningScene />
    if (matchGlobalLoads) return globalLoadsScene
    if (matchExport) return null
    return null
  }, [globalLoadsScene, matchExport, matchGlobalLoads, matchResults, matchStructuralPlanning])

  // The plan view covers the scene with the "Pläne" tab's PDF preview or the Lastplan notice
  // (see PlanPdfPreview); the scene stays mounted but must not keep rendering underneath.
  const isPlanViewSceneCovered = useEditorViewStore(selectIsPlanViewSceneCovered)
  const isSceneCovered = !!matchPlanView && isPlanViewSceneCovered

  const addOns = useMemo(() => {
    if (matchResults) return <ResultsToolbar />
    if (matchStructuralPlanning) return <StructuralPlanningToolbar />
    if (matchGlobalLoads) return <GlobalLoadsToolbar />
    if (matchIFCImport) return <IFCImportToolbar />
    // The plan view needs no page scene of its own: SceneControlled already mounts the floorplan,
    // labels, comments, tables and image cards — the page only contributes its tool bar.
    if (matchPlanView) return <PlanViewToolbar />
    return undefined
  }, [matchGlobalLoads, matchResults, matchStructuralPlanning, matchIFCImport, matchPlanView])

  const afterDrawer = useMemo(() => {
    if (!matchResults) return null
    // With the integrated anchor view, the anchors live in the Results2 "Anker" tab —
    // the Zugverbindertabelle bottom drawer is not mounted at all.
    if (results2IntegratedAnchorView) return null
    return <ResultsAfterDrawer />
  }, [matchResults, results2IntegratedAnchorView])

  // The list toggle is only meaningful on the pages that render a list pane at the outlet's edge.
  // Results2 uses its own collapse flag (and shows the list by default); the others share one.
  const isResults2 = !!matchResults
  const listCollapsed = isResults2 ? resultsListCollapsed : elementListCollapsed

  // On the results page, start the global right drawer wider.
  let outletPreferredSize = 455
  if (matchResults) {
    outletPreferredSize = resultsOutletWidth
  } else if (
    (matchStructuralPlanning || matchGlobalLoads || matchIFCImport || matchPlanView) &&
    !elementListCollapsed
  ) {
    // With the element list open, default the outlet wide enough to fit list + detail drawer, so
    // navigating back to these pages restores the wider layout instead of the narrow default.
    outletPreferredSize = expandedOutletWidth
  }

  useAllotmentRouteResize(containerRef, allotmentRef, outletPreferredSize)

  // When expanding the list, grow the outlet to fit both panes — but only if it's currently too
  // narrow. A drawer already wide enough (e.g. the user dragged it out) is left as is.
  const ensureOutletFitsList = useCallback((targetWidth: number) => {
    const el = containerRef.current
    const api = allotmentRef.current
    if (!el || !api) return
    const total = el.clientWidth
    if (!total) return
    if ((outletWidthRef.current ?? 0) >= targetWidth) return
    api.resize(computePaneSizes(total, targetWidth))
  }, [])

  const toggleListCollapsed = () => {
    if (isResults2) {
      const willExpand = resultsListCollapsed
      setResultsListCollapsed(!resultsListCollapsed)
      if (willExpand) ensureOutletFitsList(resultsOutletWidth)
      return
    }
    const willExpand = elementListCollapsed
    setElementListCollapsed(!elementListCollapsed)
    if (willExpand) ensureOutletFitsList(expandedOutletWidth)
  }
  const showElementListToggle =
    (matchStructuralPlanning ||
      matchGlobalLoads ||
      matchIFCImport ||
      matchPlanView ||
      isResults2) &&
    !ifcDrawer &&
    !aiChatOpen

  const layout = (
    // Shares the plan view's PDF preview (scene pane) with its plan rows (outlet).
    <PlanPdfViewerProvider>
      <LayoutDrawer>
        <Box ref={containerRef} sx={{ flex: 1, minWidth: 0, height: '100%', position: 'relative' }}>
          <Allotment
            id="editor-scene-allotment"
            ref={allotmentRef}
            onChange={handleAllotmentChange}
          >
            <Allotment.Pane minSize={500}>
              <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
                <SceneControlled addOns={addOns} isCovered={isSceneCovered}>
                  <ErrorBoundary
                    fallbackRender={({ error }) => (
                      <SceneErrorIndicatorFallback
                        error={error}
                        context={'Fehler beim Laden von Elementen der Szene. '}
                      />
                    )}
                  >
                    {scene}
                  </ErrorBoundary>
                </SceneControlled>
                {matchPlanView && <PlanPdfPreview />}
                {afterDrawer}
              </Box>
            </Allotment.Pane>

            <Allotment.Pane minSize={320} preferredSize={outletPreferredSize}>
              <Box
                sx={({ palette }) => ({
                  height: '100%',
                  minHeight: 0,
                  backgroundColor: palette.common.white,
                  borderLeft: `1px solid ${palette.divider}`,
                  overflow: 'auto',
                })}
              >
                {/* Hidden, not unmounted, while the chat is open: the page keeps its state
                    (unsaved forms, selection) for when the chat is closed again. */}
                <Box sx={{ display: aiChatOpen ? 'none' : 'contents' }}>
                  <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
                    <Outlet />
                  </ErrorBoundary>
                </Box>
                <AIChatDrawerSlot />
              </Box>
            </Allotment.Pane>
          </Allotment>

          {showElementListToggle && toggleReady && (
            <Tooltip
              title={listCollapsed ? 'Liste einblenden' : 'Liste ausblenden'}
              placement="top"
              arrow
              disableInteractive
            >
              <IconButton
                size="small"
                ref={node => {
                  toggleButtonRef.current = node
                  // Position on mount from the latest measured width; drag updates it imperatively.
                  if (node && scenePaneWidthRef.current !== null) {
                    node.style.left = `${scenePaneWidthRef.current}px`
                  }
                }}
                onClick={toggleListCollapsed}
                data-cy="toggle-element-list"
                aria-label={listCollapsed ? 'Liste einblenden' : 'Liste ausblenden'}
                sx={{
                  position: 'absolute',
                  bottom: 36,
                  transform: 'translateX(-50%)',
                  // Above the Allotment sash (z-index 35) so the toggle gets the click, not the drag.
                  zIndex: 40,
                  // Twice as large while collapsed, so the hidden list is easy to bring back.
                  width: listCollapsed ? 56 : 28,
                  height: listCollapsed ? 56 : 28,
                  // Same green as an active toggle button (MuiToggleButton's selected state).
                  color: 'text.primary',
                  backgroundColor: 'primary.lighter',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  boxShadow: 1,
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    boxShadow: 2,
                  },
                }}
              >
                {listCollapsed ? (
                  <ChevronLeft sx={{ fontSize: 36 }} />
                ) : (
                  <ChevronRight sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </LayoutDrawer>
    </PlanPdfViewerProvider>
  )

  return layout
}

export default EditorSceneLayout
